import React from 'react';
import { Typography, Rating } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid-pro';

import { isNumber } from 'lib/typeguards';
import logger from 'lib/logger';
import { Option } from 'lib/types';
import { IGetReviewsOptions, IReview } from '../reviews';
import { updateArrayItem } from 'lib/helpers';
import { DEFAULT_PAGE_LIMIT } from 'lib/hooks/usePagedData';
import { CUSTOMERS_VIEW, ORDERS_VIEW } from 'config/routes';
import { LinkIdCellRowModel, LinkIdCell } from 'lib/MuiGrid/LinkIdCell';
import { reviewsApi } from '../reviewsApi';
import { EClientPlatform } from 'components/audience/models/addTriggersModel';

export const ANY_RATING = 'ANY';

const formatValue = (alternative: string) => (value: string | undefined) => value || alternative;

const renderUserIdCell = (params: GridCellParams<LinkIdCellRowModel, string>) => (
    <LinkIdCell variant="body2" id={params.value} param="customerId" path={CUSTOMERS_VIEW} />
);

const renderFullNameCell = (params: GridCellParams<LinkIdCellRowModel, string>) => {
    if (!params.value || params.value === 'REDACTED') {
        return <Typography variant="body2">-</Typography>;
    }
    return <Typography variant="body2">{params.value}</Typography>;
};

const renderOrderIdCell = (params: GridCellParams<LinkIdCellRowModel, string>) => {
    if (!isNumber(params.value)) {
        return <Typography variant="body2">-</Typography>;
    }
    return <LinkIdCell variant="body2" id={params.value} param="orderId" path={ORDERS_VIEW} />;
};

const renderRatingCell = (params: GridCellParams<LinkIdCellRowModel, number>) => {
    if (!isNumber(params.value)) {
        return <Typography variant="body2">-</Typography>;
    }
    return <Rating value={params.value} readOnly />;
};

const platformFormatter = (value: string) => {
    switch (value) {
        case EClientPlatform.WEB:
            return 'Web';
        case EClientPlatform.IOS:
            return 'iOS';
        case EClientPlatform.ANDROID:
            return 'Android';
        default:
            return '-';
    }
};

const columns: GridColDef[] = [
    { field: 'rating', headerName: 'Rating', width: 150, renderCell: renderRatingCell },
    { field: 'orderId', headerName: 'Order ID', width: 100, renderCell: renderOrderIdCell, sortable: false },
    { field: 'userId', headerName: 'User ID', width: 100, renderCell: renderUserIdCell, sortable: false },
    {
        field: 'fullName',
        headerName: 'Full Name',
        width: 180,
        renderCell: renderFullNameCell,
        sortable: false
    },
    { field: 'text', headerName: 'Comment', width: 250, valueFormatter: formatValue('-'), sortable: false },
    {
        field: 'platform',
        headerName: 'Platform',
        width: 100,
        valueFormatter: platformFormatter,
        sortable: false
    },
    {
        field: 'location',
        headerName: 'Location',
        width: 180,
        valueFormatter: formatValue('-'),
        sortable: false
    },
    { field: 'updatedAt', headerName: 'Updated At', width: 100 },
    { field: 'id', headerName: 'ID', width: 150, sortable: false }
];

export const ratingOptions: Option[] = [
    {
        label: 'All Ratings',
        value: ANY_RATING
    },
    {
        label: '5 stars',
        value: '5'
    },
    {
        label: '4 stars',
        value: '4'
    },
    {
        label: '3 stars',
        value: '3'
    },
    {
        label: '2 stars',
        value: '2'
    },
    {
        label: '1 star',
        value: '1'
    }
];

export interface CustomerReviewsFilter {
    text?: string;
    locationId?: string;
    rating?: number;
    sortModel?: GridSortModel;
}

function getReviewsFilterOptions(filter: CustomerReviewsFilter): Partial<IGetReviewsOptions> {
    const options: Partial<IGetReviewsOptions> = {};
    if (filter.text) {
        options.text = filter.text;
    }
    if (filter.locationId) {
        options.locationId = filter.locationId;
    }
    if (filter.rating) {
        options.rating = filter.rating;
    }
    if (filter.sortModel && filter.sortModel[0]) {
        const sortField = filter.sortModel[0]?.field;
        const sortDirection = filter.sortModel[0]?.sort;
        options.sort = `${sortDirection === 'asc' ? '' : '-'}${sortField}` as any;
    }
    return options;
}

export function useCustomerReviewsData(filter: CustomerReviewsFilter) {
    const [loading, setLoading] = React.useState(true);
    const [endOfData, setEndOfData] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [pages, setPages] = React.useState([undefined]);
    const [reviews, setReviews] = React.useState<IReview[]>([]);
    const [internalFilter, setInternalFilter] = React.useState<CustomerReviewsFilter>();

    // Might need to make this configurable in the future
    const rowsPerPage = DEFAULT_PAGE_LIMIT;

    const reset = React.useCallback(() => {
        setReviews([]);
        setPage(0);
        setPages([undefined]);
        setLoading(true);
        setEndOfData(false);
    }, []);

    React.useEffect(() => {
        const getReviews = async () => {
            if (!internalFilter || endOfData) {
                return;
            }
            setLoading(true);
            try {
                const filterOptions = getReviewsFilterOptions(internalFilter);
                const options: IGetReviewsOptions = {
                    ...filterOptions,
                    limit: rowsPerPage
                };
                if (pages[page]) {
                    options.startKey = pages[page];
                }
                const { ok, body } = await reviewsApi.getReviews(options);
                if (!ok) {
                    throw new Error('Failed to fetch reviews');
                }
                if (!body.page.nextKey) {
                    setEndOfData(true);
                }
                setReviews(current => [
                    ...current,
                    ...body.items.map((item: IReview) => ({ ...item, id: item._id }))
                ]);
                setPages(currentPages => updateArrayItem(currentPages, page + 1, body.page.nextKey));
            } catch (e) {
                logger.error(e);
                setReviews([]);
            }
            setLoading(false);
        };
        if (reviews.length <= page * rowsPerPage && !endOfData) {
            getReviews();
        }
    }, [endOfData, internalFilter, page, pages, reviews.length, rowsPerPage]);

    React.useEffect(() => {
        setInternalFilter(filter);
        reset();
    }, [filter, reset]);

    const onPageChange = React.useCallback(
        (_: unknown, newPage: number) => {
            if (!loading) {
                setPage(newPage);
            }
        },
        [loading]
    );

    const count = React.useMemo(() => {
        if (endOfData) {
            return reviews.length;
        }
        return -1;
    }, [endOfData, reviews.length]);

    const pagedReviews = React.useMemo(() => {
        if (!Array.isArray(reviews)) {
            return [];
        }
        return reviews.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [reviews, page, rowsPerPage]);

    return {
        reviews,
        page,
        onPageChange,
        loading,
        count,
        pagedReviews,
        reset,
        endOfData,
        rowsPerPage,
        columns
    };
}
